// Bringing an Audiotool project into Seedbed (/upload/audiotool).
//
// `?project=` names it (a studio link or a project name) and `?parent=` the
// seed it continues — both arrive from the seed's Audiotool page; cold, you
// paste a link or pick one of your recent projects. With a parent it lands as
// a new version (your seed) or a remix (someone else's); without, a new song.
//
// The work runs in this tab (@audiotool-core/project-upload), the same as a
// Live import does in the extension; the bytes go through this app's own
// worker (webTransport). Afterwards the project is linked to the new seed, so
// the next import from it chains off this version.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, ChevronLeft, Loader2, X, XCircle } from "lucide-react";
import type { AuthenticatedClient } from "@audiotool/nexus";
import type { SeedApiDetail } from "@shared/api-types.js";
import { projectNameFromUrl, studioUrl } from "@shared/audiotool/link.js";
import { getProjectRow, listProjects } from "@audiotool-core/link.js";
import { Button } from "@web-shared/components/ui/button";
import { applyPushEvent, NO_PROGRESS, stageText, type ExportProgress } from "@web-shared/lib/audiotool-progress";
import { browserRenders, uploadProjectToSeedbed, webTransport, type ProjectUploadOutcome } from "@web-shared/lib/audiotool-upload";
import { relativeTime } from "@web-shared/lib/format";
import { audiotoolHubPath, audiotoolUploadPath, seedPath } from "@/lib/ctas";
import { webApi } from "@web-shared/lib/web-api";
import { SampleList } from "@web-shared/components/UploadSampleList";
import { track as trackAnalytics } from "@web-shared/lib/analytics";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSession } from "../session";
import { AudiotoolStatus, PasteLink, useAudiotoolSignIn } from "./AudiotoolHub";

type Status = "ready" | "uploading" | "done" | "error" | "cancelled";

export function AudiotoolImportPage() {
  const [params] = useSearchParams();
  useDocumentTitle("Import from Audiotool");
  const projectName = projectNameFromUrl(params.get("project") ?? "");
  const parentId = params.get("parent");
  const at = useAudiotoolSignIn();

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <Link
        to={parentId ? audiotoolHubPath(parentId) : "/"}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3.5" /> {parentId ? "back to the seed's Audiotool projects" : "back to your seeds"}
      </Link>
      <h1 className="text-lg font-semibold">Plant from Audiotool</h1>
      <AudiotoolStatus at={at} />
      {at.status === "signed-in" &&
        (projectName ? (
          <ImportForm key={`${projectName}:${parentId}`} client={at.client} projectName={projectName} parentId={parentId} />
        ) : (
          <PickProject client={at.client} parentId={parentId} userName={at.userName} />
        ))}
    </div>
  );
}

/** No project yet: paste one, or pick from the account's recent projects. */
function PickProject({ client, parentId, userName }: { client: AuthenticatedClient; parentId: string | null; userName: string }) {
  const recent = useQuery({
    queryKey: ["audiotool", "recent", userName],
    queryFn: () => listProjects(client),
    staleTime: 30_000,
  });
  return (
    <div className="space-y-4">
      <PasteLink {...(parentId ? { parent: parentId } : {})} label="Continue" />
      <section className="space-y-2 rounded-2xl border border-hairline bg-raised p-4">
        <h2 className="text-sm font-medium">Or pick one of {userName}'s recent projects</h2>
        {recent.isPending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        {recent.isError && <p className="text-sm text-removed">{recent.error.message}</p>}
        {recent.data?.length === 0 && <p className="text-sm text-muted-foreground">No projects on this Audiotool account.</p>}
        <ul className="divide-y divide-border">
          {recent.data?.map((p) => (
            <li key={p.name}>
              <Link
                to={audiotoolUploadPath({ project: p.name, ...(parentId ? { parent: parentId } : {}) })}
                className="flex items-center justify-between gap-2 py-2 text-sm hover:text-primary"
              >
                <span className="truncate">{p.displayName}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {p.updated ? `edited ${relativeTime(p.updated.toISOString())}` : ""}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ImportForm({ client, projectName, parentId }: { client: AuthenticatedClient; projectName: string; parentId: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const seedbed = useSession();

  const project = useQuery({
    queryKey: ["audiotool", "project", projectName],
    queryFn: () => getProjectRow(client, projectName),
    retry: false,
  });
  const parent = useQuery({
    queryKey: ["web", "seed", parentId ?? "", seedbed?.user.id ?? ""],
    enabled: parentId !== null,
    queryFn: () => webApi<SeedApiDetail>(`/seeds/${encodeURIComponent(parentId!)}`),
  });
  const mine = Boolean(seedbed && parent.data && seedbed.user.id === parent.data.producer.id);

  const [status, setStatus] = useState<Status>("ready");
  const [progress, setProgress] = useState<ExportProgress>(NO_PROGRESS);
  const [outcome, setOutcome] = useState<ProjectUploadOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [asChild, setAsChild] = useState(true);
  const cancelled = useRef(false);

  // The title starts as the parent's (a new version is the same song), else the project's.
  const shownTitle = title ?? parent.data?.title ?? project.data?.displayName ?? "";
  const underParent = parentId !== null && asChild && parent.data !== undefined;

  const run = useCallback(async () => {
    if (!project.data) return;
    cancelled.current = false;
    setStatus("uploading");
    setProgress(NO_PROGRESS);
    setError(null);
    try {
      const result = await uploadProjectToSeedbed(
        client,
        webTransport,
        {
          project: { name: project.data.name, displayName: project.data.displayName },
          title: shownTitle,
          parent: underParent && parent.data ? { seedId: parent.data.id, rootSeedId: parent.data.rootSeedId || parent.data.id } : null,
          note: note.trim() === "" ? null : note.trim(),
          linkOrigin: window.location.origin,
        },
        { report: (event) => setProgress((p) => applyPushEvent(p, event)), renders: browserRenders(client) },
      );
      if (cancelled.current) return;
      setOutcome(result);
      setStatus("done");
      trackAnalytics("imported_from_audiotool", { seedId: result.seedId });
      trackAnalytics("remix_completed", { seedId: result.seedId, detail: "audiotool" });
      void queryClient.invalidateQueries({ queryKey: ["web"] });
      void queryClient.invalidateQueries({ queryKey: ["audiotool", "linked"] });
    } catch (e) {
      if (cancelled.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setStatus("error");
    }
  }, [client, project.data, shownTitle, underParent, parent.data, note, queryClient]);

  // The bar creeps through the stages that have nothing to count.
  const [creep, setCreep] = useState(0);
  useEffect(() => {
    if (status !== "uploading") return;
    const t = setInterval(() => setCreep((c) => c + 0.4), 300);
    return () => clearInterval(t);
  }, [status]);
  useEffect(() => setCreep(0), [progress.stage]);
  const percent = Math.min(progress.stage === "done" ? 100 : 95, progress.percent + creep);

  if (project.isPending || (parentId !== null && parent.isPending)) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Opening the project…
      </p>
    );
  }
  if (project.isError) {
    return (
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-sm text-removed">
          <XCircle className="mt-0.5 size-4 shrink-0" /> {project.error.message}
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate(audiotoolUploadPath(parentId ? { parent: parentId } : {}))}>
          Pick another project
        </Button>
      </div>
    );
  }
  const p = project.data;

  return (
    <div className="space-y-4 rounded-2xl border border-hairline bg-raised p-4">
      <p className="text-sm">
        <a className="font-medium underline" href={studioUrl(p.name)} target="_blank" rel="noreferrer">
          {p.displayName}
        </a>
        {p.updated && <span className="text-muted-foreground"> · edited {relativeTime(p.updated.toISOString())}</span>}
      </p>

      {status === "ready" && (
        <div className="space-y-3">
          {parent.data && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={asChild} onChange={(e) => setAsChild(e.target.checked)} />
              {mine ? "New version of" : "Remix of"} "{parent.data.title}" (v{parent.data.version})
            </label>
          )}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="seed-title">Title</label>
            <input
              id="seed-title"
              value={shownTitle}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="seed-note">Note (optional)</label>
            <input
              id="seed-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What changed?"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <Button className="w-full" onClick={() => void run()}>
            {underParent ? (mine ? "Plant as new version" : "Plant as a remix") : "Plant seed"}
          </Button>
          <p className="text-xs text-muted-foreground">
            The notes, the audio, and how it all sounds come across: Audiotool renders the mix and each MIDI track's
            instrument. To render a track on its own, Seedbed mutes the others in your project for a few seconds, then puts
            everything back exactly as it was.
          </p>
        </div>
      )}

      {status === "uploading" && (
        <div className="space-y-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
              style={{ width: `${Math.max(3, Math.round(percent))}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{stageText(progress)}</p>
          <SampleList samples={progress.samples} />
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                cancelled.current = true;
                setStatus("cancelled");
              }}
            >
              <X className="mr-1 size-3.5" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {status === "done" && outcome && (
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-sm">
            <CheckCircle className="size-4 shrink-0 text-primary" />
            {outcome.audioTracks} audio + {outcome.midiTracks} MIDI track(s), {outcome.clips} clip(s) at {outcome.tempo} BPM
            {outcome.reused > 0 ? `; ${outcome.reused} already in the pool` : ""}.
          </p>
          {outcome.notes.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
              {outcome.notes.slice(0, 6).map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
          <Button asChild>
            <Link to={seedPath(outcome.seedId)}>View the seed</Link>
          </Button>
        </div>
      )}

      {(status === "error" || status === "cancelled") && (
        <div className="space-y-3">
          {error && (
            <p className="flex items-start gap-2 text-sm text-removed">
              <XCircle className="mt-0.5 size-4 shrink-0" /> <span>{error}</span>
            </p>
          )}
          <Button variant="outline" size="sm" onClick={() => setStatus("ready")}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}
